import { useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";

import { Typography, Box, Grid, makeStyles } from "@material-ui/core";

import { COLORS } from "../../../theme/colors";
import { NoDataCard, Legends } from "../../../components/parts";
import { QuantityChart } from "../../demo/components/QuantityChart";
import {
  getData,
  onlyUniqueValuesOfKeyInArray,
  isObjectEmpty,
  sumFromListOfObjects,
} from "../../../services";
import { MAX_BUBBLES_NUMBER } from "../../demo/hotels/steps/ReviewCalculationStep";
import { SolarPotentialPart } from "./SolarPotentialPart";
import { ElectricityEfficiencyPart } from "./ElectricityEfficiencyPart";
import { ElectricityConsumptionPart } from "./ElectricityConsumptionPart";
import { TotalRoofAreaPart } from "./TotalRoofAreaPart";
import { TotalBuildingAreaPart } from "./TotalBuildingAreaPart";
import usIcon from "../../../assets/images/icons/us.svg";
import euIcon from "../../../assets/images/icons/eu.svg";

const LOCATION_COLORS = {
  usa: COLORS.darks[0][70],
  europe: COLORS.crayola,
};

const LOCATION_BARS = [
  { color: COLORS.darks[0][70], key: "USA" },
  { color: COLORS.crayola, key: "EUROPE" },
];

const LOCATION_ICONS = {
  usa: usIcon,
  europe: euIcon,
};

const useStyles = makeStyles(() => ({
  divider: {
    height: 15,
    width: 2,
    marginTop: 5,
  },
  chartBox: {
    marginTop: 20,
  },
  title: {
    padding: "10px 0 0 20px",
  },
}));

export const BAR_COLORS = [
  "#A68E8E",
  "#A3B6E8",
  "#E8A3D9",
  COLORS.crayola,
  "#9D7ACF",
  "#626262",
  "#7FC7CF",
  "#C2C2C2",
];
export const OTHER_COLOR = BAR_COLORS[MAX_BUBBLES_NUMBER - 1];

const differentLocationGroup = (
  results,
  electricity_costs_2021,
  electricity_costs_2022,
  electricity_costs_2023,
  electricity_consumption_2021,
  electricity_consumption_2022,
  electricity_consumption_2023,
  electricity_costs_2021_per_square,
  electricity_costs_2022_per_square,
  electricity_costs_2023_per_square,
  electricity_consumption_2021_per_square,
  electricity_consumption_2022_per_square,
  electricity_consumption_2023_per_square,
  totalAreaChartData,
  roofAreaChartData,
  setQuantityChartData
) => {
  const totalCount = sumFromListOfObjects(results, "count");
  let usaTotalArea = null;
  let usaRoofArea = null;

  let euTotalArea = null;
  let euRoofArea = null;

  results.forEach((item) => {
    let name = item.location.toLowerCase();
    name = name.startsWith("_") ? "USA" : "EUROPE";
    if (item.electricity_costs._2021 !== null) {
      if (!(name in electricity_costs_2021)) electricity_costs_2021[name] = 0;
      electricity_costs_2021[name] += item.electricity_costs._2021;
    }
    if (item.electricity_costs._2022 !== null) {
      if (!(name in electricity_costs_2022)) electricity_costs_2022[name] = 0;
      electricity_costs_2022[name] += item.electricity_costs._2022;
    }
    if (item.electricity_costs._2023 !== null) {
      if (!(name in electricity_costs_2023)) electricity_costs_2023[name] = 0;
      electricity_costs_2023[name] += item.electricity_costs._2023;
    }

    if (item.electricity_consumption._2021 !== null) {
      if (!(name in electricity_consumption_2021)) electricity_consumption_2021[name] = 0;
      electricity_consumption_2021[name] += item.electricity_consumption._2021;
    }
    if (item.electricity_consumption._2022 !== null) {
      if (!(name in electricity_consumption_2022)) electricity_consumption_2022[name] = 0;
      electricity_consumption_2022[name] += item.electricity_consumption._2022;
    }
    if (item.electricity_consumption._2023 !== null) {
      if (!(name in electricity_consumption_2023)) electricity_consumption_2023[name] = 0;
      electricity_consumption_2023[name] += item.electricity_consumption._2023;
    }

    if ("electricity_costs_per_square" in item) {
      if (item.electricity_costs_per_square._2021 !== null) {
        if (!(name in electricity_costs_2021_per_square))
          electricity_costs_2021_per_square[name] = 0;
        electricity_costs_2021_per_square[name] += item.electricity_costs_per_square._2021;
      }
      if (item.electricity_costs_per_square._2022 !== null) {
        if (!(name in electricity_costs_2022_per_square))
          electricity_costs_2022_per_square[name] = 0;
        electricity_costs_2022_per_square[name] += item.electricity_costs_per_square._2022;
      }
      if (item.electricity_costs_per_square._2023 !== null) {
        if (!(name in electricity_costs_2023_per_square))
          electricity_costs_2023_per_square[name] = 0;
        electricity_costs_2023_per_square[name] += item.electricity_costs_per_square._2023;
      }
    }

    if ("electricity_consumption_per_square" in item) {
      if (item.electricity_consumption_per_square?._2021 !== null) {
        if (!(name in electricity_consumption_2021_per_square))
          electricity_consumption_2021_per_square[name] = 0;
        electricity_consumption_2021_per_square[name] +=
          item.electricity_consumption_per_square._2021;
      }
      if (item.electricity_consumption_per_square?._2022 !== null) {
        if (!(name in electricity_consumption_2022_per_square))
          electricity_consumption_2022_per_square[name] = 0;
        electricity_consumption_2022_per_square[name] +=
          item.electricity_consumption_per_square._2022;
      }
      if (item.electricity_consumption_per_square?._2023 !== null) {
        if (!(name in electricity_consumption_2023_per_square))
          electricity_consumption_2023_per_square[name] = 0;
        electricity_consumption_2023_per_square[name] +=
          item.electricity_consumption_per_square._2023;
      }
    }

    if (name === "USA") {
      usaTotalArea += item.sum_total_square;
      usaRoofArea += item.sum_roof_square;
    } else if (name === "EUROPE") {
      euTotalArea += item.sum_total_square;
      euRoofArea += item.sum_roof_square;
    }
    item["percent"] = (item.count * 100) / totalCount;
  });

  if (usaTotalArea !== null) {
    totalAreaChartData.push({
      name: "USA",
      value: usaTotalArea,
      color: COLORS.darks[0][70],
      unit: "unit_system",
    });
  }

  if (euTotalArea !== null) {
    totalAreaChartData.push({
      name: "EUROPE",
      value: euTotalArea,
      color: COLORS.crayola,
      unit: "unit_system",
    });
  }

  if (usaRoofArea !== null) {
    roofAreaChartData.push({
      name: "USA",
      value: usaRoofArea,
      color: COLORS.darks[0][70],
      unit: "unit_system",
    });
  }

  if (euRoofArea !== null) {
    roofAreaChartData.push({
      name: "EUROPE",
      value: euRoofArea,
      color: "#CE8464",
      unit: "unit_system",
    });
  }

  const euQuantityData = {
    location: null,
    count: 0,
    percent: 0,
  };
  const usaQuantityData = {
    location: null,
    count: 0,
    percent: 0,
  };
  results.forEach((item) => {
    if (item.location.startsWith("_")) {
      usaQuantityData.location = "USA";
      usaQuantityData.count += item.count;
      usaQuantityData.percent += item.percent;
    } else {
      euQuantityData.location = "EUROPE";
      euQuantityData.count += item.count;
      euQuantityData.percent += item.percent;
    }
  });
  const quantityData = [];
  if (euQuantityData.location) quantityData.push(euQuantityData);
  if (usaQuantityData.location) quantityData.push(usaQuantityData);
  setQuantityChartData(quantityData);
};

let locationGroup = (
  results,
  electricity_costs_2021,
  electricity_costs_2022,
  electricity_costs_2023,
  electricity_consumption_2021,
  electricity_consumption_2022,
  electricity_consumption_2023,
  electricity_costs_2021_per_square,
  electricity_costs_2022_per_square,
  electricity_costs_2023_per_square,
  electricity_consumption_2021_per_square,
  electricity_consumption_2022_per_square,
  electricity_consumption_2023_per_square,
  totalAreaChartData,
  roofAreaChartData,
  setQuantityChartData,
  setBars,
  setBubbleColors
) => {
  const totalCount = sumFromListOfObjects(results, "count");
  results.forEach((item) => (item.percent = (item.count * 100) / totalCount));
  results.sort((a, b) => b.percent - a.percent);
  const mainResults = results.slice(0, MAX_BUBBLES_NUMBER - 1);
  const otherResults = results.slice(MAX_BUBBLES_NUMBER - 1);

  const temporaryBars = [];
  const temporaryBubbleColors = [];
  const otherResultsBubbleData = [];

  mainResults.forEach((item, i) => {
    const name = item.location.toUpperCase().replace("_", "");
    item.location = name;
    temporaryBars.push({
      key: name,
      color: BAR_COLORS[i],
    });
    temporaryBubbleColors[name.toLowerCase()] = BAR_COLORS[i];

    if (item.electricity_costs._2021 !== null)
      electricity_costs_2021[name] = item.electricity_costs._2021;
    if (item.electricity_costs._2022 !== null)
      electricity_costs_2022[name] = item.electricity_costs._2022;
    if (item.electricity_costs._2023 !== null)
      electricity_costs_2023[name] = item.electricity_costs._2023;

    if (item.electricity_consumption._2021 !== null)
      electricity_consumption_2021[name] = item.electricity_consumption._2021;
    if (item.electricity_consumption._2022 !== null)
      electricity_consumption_2022[name] = item.electricity_consumption._2022;
    if (item.electricity_consumption._2023 !== null)
      electricity_consumption_2023[name] = item.electricity_consumption._2023;

    if ("electricity_costs_per_square" in item) {
      if (item.electricity_costs_per_square._2021 !== null)
        electricity_costs_2021_per_square[name] = item.electricity_costs_per_square._2021;
      if (item.electricity_costs_per_square._2022 !== null)
        electricity_costs_2022_per_square[name] = item.electricity_costs_per_square._2022;
      if (item.electricity_costs_per_square._2023 !== null)
        electricity_costs_2023_per_square[name] = item.electricity_costs_per_square._2023;
    }

    if ("electricity_consumption_per_square" in item) {
      if (item.electricity_consumption_per_square?._2021 !== null)
        electricity_consumption_2021_per_square[name] =
          item.electricity_consumption_per_square._2021;
      if (item.electricity_consumption_per_square?._2022 !== null)
        electricity_consumption_2022_per_square[name] =
          item.electricity_consumption_per_square._2022;
      if (item.electricity_consumption_per_square?._2023 !== null)
        electricity_consumption_2023_per_square[name] =
          item.electricity_consumption_per_square._2023;
    }

    totalAreaChartData.push({
      name: name,
      value: item.sum_total_square,
      color: BAR_COLORS[i],
      unit: "unit_system",
    });

    roofAreaChartData.push({
      name: name,
      value: item.sum_roof_square,
      color: BAR_COLORS[i],
      unit: "unit_system",
    });
  });

  if (otherResults.length) {
    temporaryBars.push({
      key: "others",
      color: OTHER_COLOR,
    });
    temporaryBubbleColors["others"] = OTHER_COLOR;
    otherResultsBubbleData.location = "others";
    otherResultsBubbleData.count = 0;
    otherResultsBubbleData.percent = 0;

    electricity_costs_2021.others = 0;
    electricity_costs_2022.others = 0;
    electricity_costs_2023.others = 0;

    electricity_consumption_2021.others = 0;
    electricity_consumption_2022.others = 0;
    electricity_consumption_2023.others = 0;

    electricity_costs_2021_per_square.others = 0;
    electricity_costs_2022_per_square.others = 0;
    electricity_costs_2023_per_square.others = 0;

    electricity_consumption_2021_per_square.others = 0;
    electricity_consumption_2022_per_square.others = 0;
    electricity_consumption_2023_per_square.others = 0;

    const totalArea = {
      name: "Others",
      value: 0,
      color: OTHER_COLOR,
      unit: "unit_system",
    };

    const roofArea = {
      name: "Others",
      value: 0,
      color: OTHER_COLOR,
      unit: "unit_system",
    };

    otherResults.forEach((item) => {
      if (item.electricity_costs._2021 !== null)
        electricity_costs_2021["others"] += item.electricity_costs._2021;
      if (item.electricity_costs._2022 !== null)
        electricity_costs_2022["others"] += item.electricity_costs._2022;
      if (item.electricity_costs._2023 !== null)
        electricity_costs_2023["others"] += item.electricity_costs._2023;

      if (item.electricity_consumption._2021 !== null)
        electricity_consumption_2021["others"] += item.electricity_consumption._2021;
      if (item.electricity_consumption._2022 !== null)
        electricity_consumption_2022["others"] += item.electricity_consumption._2022;
      if (item.electricity_consumption._2023 !== null)
        electricity_consumption_2023["others"] += item.electricity_consumption._2023;

      if (item.electricity_costs_per_square._2021 !== null)
        electricity_costs_2021_per_square["others"] += item.electricity_costs_per_square._2021;
      if (item.electricity_costs_per_square._2022 !== null)
        electricity_costs_2022_per_square["others"] += item.electricity_costs_per_square._2022;
      if (item.electricity_costs_per_square._2023 !== null)
        electricity_costs_2023_per_square["others"] += item.electricity_costs_per_square._2023;

      if (item.electricity_consumption_per_square._2021 !== null)
        electricity_consumption_2021_per_square["others"] +=
          item.electricity_consumption_per_square._2021;
      if (item.electricity_consumption_per_square._2022 !== null)
        electricity_consumption_2022_per_square["others"] +=
          item.electricity_consumption_per_square._2022;
      if (item.electricity_consumption_per_square._2023 !== null)
        electricity_consumption_2023_per_square["others"] +=
          item.electricity_consumption_per_square._2023;

      if (item.sum_total_square !== null) totalArea.value += item.sum_total_square;
      if (item.sum_roof_square !== null) roofArea.value += item.sum_roof_square;

      otherResultsBubbleData.count += item.count;
      otherResultsBubbleData.percent += item.percent;
    });

    totalAreaChartData.push(totalArea);
    roofAreaChartData.push(roofArea);
  }

  setBars(temporaryBars);
  setBubbleColors(temporaryBubbleColors);

  let bubbleData = [...mainResults];
  if (!isObjectEmpty(otherResultsBubbleData)) bubbleData.push(otherResultsBubbleData);
  setQuantityChartData(bubbleData);
};

const defaultData = {
  count: null,
  europe_country_count: null,
  results: [],
  usa_state_count: null,
};

export const LocationTab = ({ setCount, resetSteps }) => {
  const classes = useStyles();
  const { clusters } = useSelector((state) => state.auth);
  const preliminaryStage = clusters[0].preliminary_stage;
  const [electricityCostsChartData, setElectricityCostsChartData] = useState([]);
  const [electricityConsumptionChartData, setElectricityConsumptionChartData] = useState([]);
  const [electricityCostsPerSquareChartData, setElectricityCostsPerSquareChartData] = useState([]);
  const [electricityConsumptionPerSquareChartData, setElectricityConsumptionPerSquareChartData] =
    useState([]);
  const [totalAreaChartData, setTotalAreaChartData] = useState([]);
  const [roofAreaChartData, setRoofAreaChartData] = useState([]);
  const bubbleChartContainerRef = useRef();
  const [quantityChartData, setQuantityChartData] = useState([]);
  const [bars, setBars] = useState(LOCATION_BARS);
  const [bubbleColors, setBubbleColors] = useState(LOCATION_COLORS);
  const [data, setData] = useState(defaultData);
  useEffect(() => {
    getData(`onboarding/base/analytics-by-location/?clusters=${clusters[0].id}`, (data) => {
      setCount(data.count);
      setData(data);

      const electricity_costs_2021 = {};
      const electricity_costs_2022 = {};
      const electricity_costs_2023 = {};

      const electricity_consumption_2021 = {};
      const electricity_consumption_2022 = {};
      const electricity_consumption_2023 = {};

      const electricity_costs_2021_per_square = {};
      const electricity_costs_2022_per_square = {};
      const electricity_costs_2023_per_square = {};

      const electricity_consumption_2021_per_square = {};
      const electricity_consumption_2022_per_square = {};
      const electricity_consumption_2023_per_square = {};

      const totalAreaChartData = [];
      const roofAreaChartData = [];

      let groupData = locationGroup;
      if (data.usa_state_count && data.europe_country_count) {
        groupData = differentLocationGroup;
      }

      groupData(
        data.results,
        electricity_costs_2021,
        electricity_costs_2022,
        electricity_costs_2023,
        electricity_consumption_2021,
        electricity_consumption_2022,
        electricity_consumption_2023,
        electricity_costs_2021_per_square,
        electricity_costs_2022_per_square,
        electricity_costs_2023_per_square,
        electricity_consumption_2021_per_square,
        electricity_consumption_2022_per_square,
        electricity_consumption_2023_per_square,
        totalAreaChartData,
        roofAreaChartData,
        setQuantityChartData,
        setBars,
        setBubbleColors
      );

      if (!onlyUniqueValuesOfKeyInArray(totalAreaChartData, "value", null)) {
        setTotalAreaChartData(totalAreaChartData);
      }
      if (!onlyUniqueValuesOfKeyInArray(roofAreaChartData, "value", null)) {
        setRoofAreaChartData(roofAreaChartData);
      }

      const electricityCosts = [];

      if (!isObjectEmpty(electricity_costs_2021)) {
        electricity_costs_2021["x"] = "2024";
        electricityCosts.push(electricity_costs_2021);
      }

      if (!isObjectEmpty(electricity_costs_2022)) {
        electricity_costs_2022["x"] = "2025";
        electricityCosts.push(electricity_costs_2022);
      }

      if (!isObjectEmpty(electricity_costs_2023)) {
        electricity_costs_2023["x"] = "2026";
        electricityCosts.push(electricity_costs_2023);
      }

      if (electricityCosts.length) {
        setElectricityCostsChartData([
          electricity_costs_2021,
          electricity_costs_2022,
          electricity_costs_2023,
        ]);
      }

      const electricityConsumption = [];
      if (!isObjectEmpty(electricity_consumption_2021)) {
        electricity_consumption_2021["x"] = "2024";
        electricity_consumption_2021["unit"] = "kwh";
        electricityConsumption.push(electricity_consumption_2021);
      }

      if (!isObjectEmpty(electricity_consumption_2022)) {
        electricity_consumption_2022["x"] = "2025";
        electricity_consumption_2022["unit"] = "kwh";
        electricityConsumption.push(electricity_consumption_2022);
      }

      if (!isObjectEmpty(electricity_consumption_2023)) {
        electricity_consumption_2023["x"] = "2026";
        electricity_consumption_2023["unit"] = "kwh";
        electricityConsumption.push(electricity_consumption_2023);
      }

      if (electricityConsumption.length) {
        setElectricityConsumptionChartData([
          electricity_consumption_2021,
          electricity_consumption_2022,
          electricity_consumption_2023,
        ]);
      }

      const electricityCostsPerSquare = [];

      if (!isObjectEmpty(electricity_costs_2021_per_square)) {
        electricity_costs_2021_per_square["x"] = "2024";
        electricityCostsPerSquare.push(electricity_costs_2021_per_square);
      }

      if (!isObjectEmpty(electricity_costs_2022_per_square)) {
        electricity_costs_2022_per_square["x"] = "2025";
        electricityCostsPerSquare.push(electricity_costs_2022_per_square);
      }

      if (!isObjectEmpty(electricity_costs_2023_per_square)) {
        electricity_costs_2023_per_square["x"] = "2026";
        electricityCostsPerSquare.push(electricity_costs_2023_per_square);
      }

      if (electricityCostsPerSquare.length) {
        setElectricityCostsPerSquareChartData([
          electricity_costs_2021_per_square,
          electricity_costs_2022_per_square,
          electricity_costs_2023_per_square,
        ]);
      }

      const electricityConsumptionPerSquare = [];
      if (!isObjectEmpty(electricity_consumption_2021_per_square)) {
        electricity_consumption_2021_per_square["x"] = "2024";
        electricity_consumption_2021_per_square["unit"] = "kwh";
        electricityConsumptionPerSquare.push(electricity_consumption_2021_per_square);
      }

      if (!isObjectEmpty(electricity_consumption_2022_per_square)) {
        electricity_consumption_2022_per_square["x"] = "2025";
        electricity_consumption_2022_per_square["unit"] = "kwh";
        electricityConsumptionPerSquare.push(electricity_consumption_2022_per_square);
      }

      if (!isObjectEmpty(electricity_consumption_2023_per_square)) {
        electricity_consumption_2023_per_square["x"] = "2026";
        electricity_consumption_2023_per_square["unit"] = "kwh";
        electricityConsumptionPerSquare.push(electricity_consumption_2023_per_square);
      }

      if (electricityConsumptionPerSquare.length) {
        setElectricityConsumptionPerSquareChartData([
          electricity_consumption_2021_per_square,
          electricity_consumption_2022_per_square,
          electricity_consumption_2023_per_square,
        ]);
      }
    }).catch((error) => {
      if (error?.response.status === 404) resetSteps();
    });

    return () => {
      setElectricityCostsChartData([]);
      setElectricityConsumptionChartData([]);
      setElectricityCostsPerSquareChartData([]);
      setElectricityConsumptionPerSquareChartData([]);
      setTotalAreaChartData([]);
      setRoofAreaChartData([]);
      setQuantityChartData([]);
      setData(defaultData);
    };
  }, []);

  return (
    <Box>
      <Legends legends={bars.map((bar) => ({ name: bar.key, color: bar.color }))} />
      <Grid container spacing={2} style={{ marginBottom: 24, marginTop: 14 }}>
        <Grid item xs={4}>
          <Box style={{ backgroundColor: COLORS.darks[0][3], borderRadius: 8, height: "100%" }}>
            <Typography variant="h4" className={classes.title}>
              Number of Locations
            </Typography>
            <Box
              ref={bubbleChartContainerRef}
              display="flex"
              justifyContent="center"
              alignItems="center"
              className={classes.chartBox}
            >
              {quantityChartData.length ? (
                <QuantityChart
                  data={quantityChartData}
                  containerRef={bubbleChartContainerRef}
                  bubbleKey="location"
                  bubbleColors={bubbleColors}
                  icons={LOCATION_ICONS}
                />
              ) : (
                <NoDataCard withShaDowBox={false} gridStyle={{ height: 160 }} />
              )}
            </Box>
          </Box>
        </Grid>
        <Grid item xs={4}>
          <TotalBuildingAreaPart totalAreaChartData={totalAreaChartData} />
        </Grid>
        <Grid item xs={4}>
          <TotalRoofAreaPart roofAreaChartData={roofAreaChartData} />
        </Grid>
      </Grid>
      {preliminaryStage?.owner === "TENANTS_CONSUMPTION" &&
      !electricityCostsChartData.length &&
      !electricityConsumptionChartData.length ? null : (
        <ElectricityConsumptionPart
          electricityCostsChartData={electricityCostsChartData}
          electricityConsumptionChartData={electricityConsumptionChartData}
          bars={bars}
        />
      )}
      {preliminaryStage?.owner === "OWN_CONSUMPTION" ? (
        <ElectricityEfficiencyPart
          electricityCostsPerSquareChartData={electricityCostsPerSquareChartData}
          electricityConsumptionPerSquareChartData={electricityConsumptionPerSquareChartData}
          bars={bars}
        />
      ) : null}
      {data.results.length ? (
        <SolarPotentialPart data={data} groupKey="location" colors={LOCATION_COLORS} />
      ) : null}
    </Box>
  );
};
